import { createElement } from 'react';
import { TbSettings } from 'react-icons/tb';
import { roles } from '@nxsys/store/constants';
import { RoleValue, RouterConfigData } from '@nxsys/types/models/Apps';

export type UserRoleFlags = {
  isAdmin: boolean;
  isEmployee: boolean;
  isBureauEmployer: boolean;
  isAgency: boolean;
};

const roleFlagMap: Partial<Record<RoleValue, keyof UserRoleFlags>> = {
  [roles.admin]: 'isAdmin',
  [roles.employee]: 'isEmployee',
  [roles.employer]: 'isBureauEmployer',
  [roles.agency]: 'isAgency',
};

function isItemVisible(
  item: RouterConfigData,
  flags: UserRoleFlags,
  permissions: string[],
): boolean {
  // Divider is purely structural — always show
  if (item.type === 'divider') return true;

  // Check role first
  if (item.allowedRoles && item.allowedRoles.length > 0) {
    const roleMatch = item.allowedRoles.some((role) => {
      const flag = roleFlagMap[role];
      return flag ? flags[flag] : false;
    });
    if (!roleMatch) return false;
  }

  // Admin always bypasses permission checks
  // if (flags.isAdmin) return true;

  // Skip permission check if user's role is in noCheckPermissionWith
  if (item.noCheckPermissionWith && item.noCheckPermissionWith.length > 0) {
    const shouldSkip = item.noCheckPermissionWith.some((role) => {
      const flag = roleFlagMap[role];
      return flag ? flags[flag] : false;
    });
    if (shouldSkip) return true;
  }

  // Check permission (array)
  if (item.permissionNames && item.permissionNames.length > 0) {
    return item.permissionNames.some((pn) => permissions.includes(pn));
  }

  // Check permission (single)
  if (item.permissionName && item.permissionName !== 'OPEN') {
    return permissions.includes(item.permissionName);
  }

  return false;
}

/** Menu items hidden for Payroll employer portal users */
const PAYROLL_EMPLOYER_HIDDEN_IDS = new Set([
  'timesheets/awaiting-invoice-generation',
]);

/** Menu items hidden for ALL Payroll system users (Agency concept doesn't exist) */
const PAYROLL_HIDDEN_IDS = new Set([
  'import/agencies',
]);

/**
 * Menu items hidden for Ireland **bureau** only (invoice generation + payroll
 * overview not applicable there). Umbrella-Ireland needs both: it is exactly
 * the screen an Irish umbrella tenant uses to invoice agencies, and `payroll`
 * is a no-op on umbrella anyway (guarded by `isBureau` below, not repeated).
 */
const IRELAND_HIDDEN_IDS = new Set([
  'timesheets/awaiting-invoice-generation',
  'payroll',
]);

/** Sidebar messageId overrides for Payroll system (isBureau=true) */
const PAYROLL_MESSAGE_OVERRIDES: Record<string, string> = {
  'sidebar.app.timesheets.pendingAgencyApproval':
    'sidebar.app.timesheets.pendingApproval',
};

export function filterMenuByRole(
  routes: RouterConfigData[],
  flags: UserRoleFlags,
  permissions: string[] = [],
  isBureau?: boolean,
  isIreland?: boolean,
): RouterConfigData[] {
  const isEmployer = flags.isBureauEmployer && !flags.isAdmin;
  const result = routes.reduce<RouterConfigData[]>((acc, item) => {
    if (!isItemVisible(item, flags, permissions)) return acc;
    if (isEmployer && PAYROLL_EMPLOYER_HIDDEN_IDS.has(item.id)) return acc;
    if (isBureau && PAYROLL_HIDDEN_IDS.has(item.id)) return acc;
    if (isBureau && isIreland && IRELAND_HIDDEN_IDS.has(item.id)) return acc;

    let processedItem = item;
    // Apply Payroll-specific sidebar label overrides
    if (isBureau && item.messageId && PAYROLL_MESSAGE_OVERRIDES[item.messageId]) {
      processedItem = { ...item, messageId: PAYROLL_MESSAGE_OVERRIDES[item.messageId] };
    }

    if (processedItem.children && processedItem.children.length > 0) {
      const filteredChildren = filterMenuByRole(processedItem.children, flags, permissions, isBureau, isIreland);
      if (
        filteredChildren.length === 0 &&
        (processedItem.type === 'group' || processedItem.type === 'collapse')
      ) {
        return acc;
      }
      acc.push({ ...processedItem, children: filteredChildren });
    } else {
      acc.push(processedItem);
    }
    return acc;
  }, []);

  // Employer portal: rename "Employers" → "Settings" with cog icon, move to bottom
  if (isEmployer) {
    const companiesIdx = result.findIndex((item) => item.id === 'companies');
    if (companiesIdx !== -1) {
      const companiesItem = {
        ...result[companiesIdx],
        messageId: 'common.settings',
        title: 'Settings',
        icon: createElement(TbSettings),
      };
      result.splice(companiesIdx, 1);
      result.push(companiesItem);
    }
  }

  return result;
}
